import { useState } from "react";
import ScheduleEditor from "./ScheduleEditor";
import { COMPENSATION_LABELS, emptySchedule } from "../../utils/timesheets";

const inputClass =
  "w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block text-xs font-medium text-gray-600 mb-1";

function blankForm(defaultMemberId) {
  return {
    member_id: defaultMemberId || "",
    compensation_type: "hourly",
    description: "",
    start_date: "",
    end_date: "",
    rate: "",
    reference_member_id: "",
    break_prefill: false,
    break_threshold_hours: "6",
    break_minutes: "30",
    monthly_max_cost: "",
    schedule: null,
  };
}

function formFromLine(line) {
  return {
    member_id: line.member_id,
    compensation_type: line.compensation_type,
    description: line.description || "",
    start_date: line.start_date || "",
    end_date: line.end_date || "",
    rate: line.rate ?? "",
    reference_member_id: line.reference_member_id || "",
    break_prefill: !!line.break_prefill,
    break_threshold_hours: String(Number(line.break_threshold_hours ?? 6)),
    break_minutes: String(line.break_minutes ?? 30),
    monthly_max_cost: line.monthly_max_cost ?? "",
    schedule: line.schedule || null,
  };
}

/**
 * Formulier voor het aanmaken of wijzigen van een contractlijn.
 * Het vergoedingstype bepaalt welke velden getoond worden. Bij wijzigen kunnen
 * de persoon en het vergoedingstype niet meer aangepast worden.
 */
export default function ContractLineForm({ line, staff, doctors, defaultMemberId, saving, onSubmit, onCancel }) {
  const editing = !!line;
  const [form, setForm] = useState(editing ? formFromLine(line) : blankForm(defaultMemberId));
  const [error, setError] = useState("");

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const isSalaried = form.compensation_type === "salaried";

  const changeType = (e) => {
    const type = e.target.value;
    setForm({
      ...form,
      compensation_type: type,
      rate: type === "salaried" ? "" : form.rate,
      reference_member_id: type === "salaried" ? "" : form.reference_member_id,
      break_prefill: type === "salaried",
      schedule: type === "salaried" ? form.schedule || emptySchedule(1) : null,
    });
  };

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!form.member_id) return setError("Kies een personeelslid.");
    if (!form.description.trim()) return setError("Geef een omschrijving in.");
    if (!form.start_date) return setError("Geef een begindatum in.");
    if (!isSalaried && form.rate === "") return setError("Geef een tarief in.");
    if (isSalaried && form.schedule?.cycle_weeks === 2 && !form.schedule.anchor_date) {
      return setError("Kies de maandag van week 1 voor het tweewekelijkse rooster.");
    }

    const payload = {
      description: form.description.trim(),
      start_date: form.start_date,
      end_date: form.end_date || null,
      rate: isSalaried ? null : Number(form.rate),
      reference_member_id: isSalaried ? null : form.reference_member_id || null,
      break_prefill: form.break_prefill,
      break_threshold_hours: Number(form.break_threshold_hours || 0),
      break_minutes: Number(form.break_minutes || 0),
      monthly_max_cost: form.monthly_max_cost === "" ? null : Number(form.monthly_max_cost),
      schedule: isSalaried ? form.schedule : null,
    };
    if (!editing) {
      payload.member_id = form.member_id;
      payload.compensation_type = form.compensation_type;
    }
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-40 overflow-y-auto p-4">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-3xl my-8 p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">
          {editing ? "Contractlijn wijzigen" : "Nieuwe contractlijn"}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Personeelslid</label>
            <select value={form.member_id} onChange={set("member_id")} disabled={editing} className={inputClass}>
              <option value="">Selecteer...</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.naam}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Vergoeding</label>
            <select value={form.compensation_type} onChange={changeType} disabled={editing} className={inputClass}>
              {Object.entries(COMPENSATION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Omschrijving</label>
            <input
              type="text"
              value={form.description}
              onChange={set("description")}
              placeholder={form.compensation_type === "per_item" ? "Bv. PDT (2 zones)" : "Bv. Bijstand arts"}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Begindatum</label>
            <input type="date" value={form.start_date} onChange={set("start_date")} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Einddatum (optioneel)</label>
            <input type="date" value={form.end_date} onChange={set("end_date")} className={inputClass} />
          </div>
          {!isSalaried && (
            <div>
              <label className={labelClass}>
                {form.compensation_type === "per_item" ? "Tarief per handeling (€)" : "Uurtarief (€)"}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.rate}
                onChange={set("rate")}
                className={inputClass}
              />
            </div>
          )}

          {!isSalaried && (
            <div>
              <label className={labelClass}>Arts (optioneel)</label>
              <select value={form.reference_member_id} onChange={set("reference_member_id")} className={inputClass}>
                <option value="">— Geen —</option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>{d.naam}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className={labelClass}>Maandmaximum (€, optioneel)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.monthly_max_cost}
              onChange={set("monthly_max_cost")}
              disabled={isSalaried}
              className={inputClass}
            />
          </div>
        </div>

        {form.compensation_type !== "per_item" && (
          <fieldset className="mt-4 border border-gray-200 rounded p-3">
            <legend className="text-xs font-medium text-gray-600 px-1">Standaardpauze</legend>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input type="checkbox" checked={form.break_prefill} onChange={set("break_prefill")} />
              Pauze automatisch voorinvullen
            </label>
            <div className="flex flex-wrap gap-4 items-center text-sm text-gray-700">
              <span>Vanaf</span>
              <input
                type="number" step="0.25" min="0" value={form.break_threshold_hours}
                onChange={set("break_threshold_hours")} className={`${inputClass} !w-20`}
              />
              <span>uur gewerkt:</span>
              <input
                type="number" step="1" min="0" value={form.break_minutes}
                onChange={set("break_minutes")} className={`${inputClass} !w-20`}
              />
              <span>minuten</span>
            </div>
          </fieldset>
        )}

        {isSalaried && (
          <fieldset className="mt-4 border border-gray-200 rounded p-3">
            <legend className="text-xs font-medium text-gray-600 px-1">Weekrooster (basisuren)</legend>
            <ScheduleEditor value={form.schedule} onChange={(schedule) => setForm({ ...form, schedule })} />
          </fieldset>
        )}

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50">
            Annuleren
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </form>
    </div>
  );
}
